import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { config } from '../config';

// Banner type definitions matching backend response
export type BannerSeverity = 'info' | 'warning' | 'error';
export type CtaType = 'internal' | 'external' | 'action';

export interface BannerCta {
  text: string;
  href: string;
  type: CtaType;
}

export interface Banner {
  id: string;
  severity: BannerSeverity;
  title: string;
  message: string;
  cta?: BannerCta;
  dismissible: boolean;
  priority: number;
}

interface BannersResponse {
  banners: Banner[];
}

const backendApi = config.backendApi;

// Fetch all active banners for the authenticated user
const fetchBanners = async (): Promise<Banner[]> => {
  const response = await axios.get<BannersResponse>(`${backendApi}/api/banners`, {
    withCredentials: true,
  });
  return response.data.banners;
};

/**
 * Custom hook to fetch and manage banners
 * Polls every 5 minutes for updates
 * @param enabled - Whether to enable the query (should be false when not authenticated)
 */
export function useBanners(enabled = true) {
  return useQuery<Banner[]>({
    queryKey: ['banners'],
    queryFn: fetchBanners,
    enabled, // Only fetch when enabled
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    refetchInterval: 5 * 60 * 1000, // Poll every 5 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    retry: 1, // Retry once on failure
  });
}
